import { Distributor } from '@larvitu/shared/react/components/feature-distributor';
import { Children, isValidElement } from 'react';

import type { ReactElement } from 'react';

interface Id {
  id?: string;
}

interface ReactElementProps<T> {
  data: T;
  key: string;
  index: number;
}

type RenderProps<T> = (item: T, key: string, index: number) => ReactElement;

export declare module forEach {
  interface Iterable<T> {
    data?: T;
  }

  type Props<T, P> =
    & {
      each?: T[];
      /** @deprecated */
      items?: T[]; /* to ensure legacy compatibility with the <ForEach/> api */
      children?:
        | RenderProps<
          P extends Omit<{ [Key in keyof P]: P[Key] }, 'children' | 'each' | 'items'> ? T
            : { data: T } & Omit<P, 'children' | 'each' | 'items'>
        >
        | ReactElement<ReactElementProps<T & P>>
        | Array<ReactElement<ReactElementProps<T & P>>>;
    }
    & { [Key in keyof P]: P[Key] };
}

export const isRenderProps = <T,>(
  children:
    | RenderProps<T>
    | ReactElement<ReactElementProps<T>>
    | Array<ReactElement<ReactElementProps<T>>>
): children is RenderProps<T> => typeof children === 'function';

export const createKey = (
  obj: { id?: string } | undefined,
  index: number
): string => (typeof obj === 'object' ? obj.id ?? `${index}` : `${index}`);

// todo: infer restprops from the child props
// todo: add restprops to renderprops as well

/**
 *  is cloning its children and passing a data object to its children for each element of the items array
 */
export const For = <
  T extends Id & { [Key in keyof T]: T[Key] } | number | string,
  P extends { [Key in keyof P]: P[Key] },
>({
  items = [],
  each = items, /* to ensure legacy compatibility with the <ForEach/> api */
  children,
  ...props
}: forEach.Props<T, P>):
  | ReactElement[]
  | null =>
{
  try {
    const child = children;
    // const child = Children.only( children );

    // if (!isValidElement( child )) return null;

    if (isRenderProps( child )) {
      return each.map( (item, index) => {
        return (
          child(
            typeof item === 'object' ? { ...item, ...props } : item,
            typeof item === 'object' ? item.id ?? `${index}` : `${index}`,
            index
          )
        );
      } );
    }

    return each.map((item, index): ReactElement<ReactElementProps<T & P>> | null => (
      <Distributor
        key={typeof item === 'object' ? item.id ?? `${index}` : `${index}`}
        data={item}
        index={index}
        {...props}
      >
        {child}
      </Distributor>
    ));
  } catch (error) {
    console.error( error );
    return null;
  }
};
